// shared/numeric/numeric-locale.service.ts

import { computed, inject, Injectable, Signal, signal } from '@angular/core';

import { CommonDataService } from '../../services/common-data.service';

/** Single source of truth for the app currency; not user-configurable. */
const DEFAULT_CURRENCY_CODE = 'USD';

/**
 * Locale utility surface used by the numeric mask stack.
 *
 * Locale and precision are snapshotted from `CommonDataService` once, at service
 * construction. The app applies these settings during startup (preferences load)
 * before any masked field mounts, so a single read is enough. Runtime changes to
 * `setCurrentApplicationCulture` / `setDecimalPrecision` intentionally do NOT
 * propagate to existing masks — a full page refresh is required.
 *
 * Values are still exposed as signals so directives can consume them uniformly
 * (the effect just never re-fires for these dependencies).
 *
 * Never hardcode `.` or `,` downstream — always read separators from here.
 */
@Injectable({ providedIn: 'root' })
export class NumericLocaleService {
    private readonly commonData = inject(CommonDataService);

    private readonly _locale = signal(this.commonData.getCurrentApplicationCulture());
    private readonly _precision = signal(this.commonData.getDecimalPrecision());
    private readonly _currencyCode = signal(DEFAULT_CURRENCY_CODE);

    readonly locale: Signal<string> = this._locale.asReadonly();

    /** App-wide ISO 4217 currency code. Owned here so callers never pass one in. */
    readonly currencyCode: Signal<string> = this._currencyCode.asReadonly();

    /** App-wide default fraction-digit count for decimals/currency/percentage. */
    readonly precision: Signal<number> = this._precision.asReadonly();

    readonly decimalSeparator = computed(() => this.separator('decimal', '.'));
    readonly groupSeparator = computed(() => this.separator('group', ','));

    /**
     * Locale-aware currency symbol + position for the app currency, e.g.
     *   en-US + USD => { symbol: '$',  position: 'prefix' }
     *   fr-CA + USD => { symbol: '$',  position: 'suffix' }
     */
    readonly currencyDisplay = computed<{ symbol: string; position: 'prefix' | 'suffix' }>(() => {
        const currencyCode = this.currencyCode();
        const parts = new Intl.NumberFormat(this.locale(), {
            style: 'currency',
            currency: currencyCode,
            currencyDisplay: 'narrowSymbol',
        }).formatToParts(1);
        const currencyIdx = parts.findIndex(p => p.type === 'currency');
        const integerIdx = parts.findIndex(p => p.type === 'integer');
        const symbol = currencyIdx >= 0 ? parts[currencyIdx].value : currencyCode;
        const position: 'prefix' | 'suffix' =
            currencyIdx >= 0 && integerIdx >= 0 && currencyIdx < integerIdx ? 'prefix' : 'suffix';
        return { symbol, position };
    });

    private separator(type: 'decimal' | 'group', fallback: string): string {
        // `12345.6` guarantees both a group and a decimal part appear in the output.
        const parts = new Intl.NumberFormat(this.locale()).formatToParts(12345.6);
        return parts.find(p => p.type === type)?.value ?? fallback;
    }
}


